/**
 * TransferHandler - Manages call transfer operations via SIP REFER
 *
 * Responsibilities:
 * - Validate preconditions (call exists, session established, domain available)
 * - Construct a sanitized SIP URI for the transfer destination
 * - Send SIP REFER to redirect the call to the target
 * - Remove the call from the store on successful transfer
 * - Execute warm (attended) transfer via SIP REFER with Replaces
 * - Record diagnostic evidence for both, without altering any of the above
 *
 * Follows the same pattern as ConferenceHandler adapted for single-call blind transfer.
 */

import { DebugService } from 'Modules/softphone/services/DebugService';
import { EventService } from 'Modules/softphone/services/EventService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import { createLogger } from 'Modules/softphone/utils/logger';
import type { Notification } from 'sip.js';
import { SessionState as SipSessionState, UserAgent } from 'sip.js';

import { buildTransferUri } from './buildTransferUri';

const logger = createLogger('CallManager:TransferHandler');

/**
 * Emit a diagnostic event without ever being able to affect the transfer.
 *
 * Diagnostics are an observer here: a failure to record something must never
 * turn into a failed transfer for the user.
 */
const emit = (message: string, details: Record<string, unknown>): void => {
  try {
    // The Call channel, not SIP: the REFER and its NOTIFYs are traced off the
    // socket by SipService. What is recorded here is what the transfer did —
    // why nothing was sent, which leg carried it, what the sipfrag said — and
    // repeating it as SIP would show two rows for one message.
    EventService.getInstance().logCallEvent(message, details);
  } catch (error) {
    logger.warn('Failed to record transfer event', {
      message,
      error: (error as Error).message,
    });
  }
};

/**
 * Capture a diagnostic record for a leg that is about to be removed.
 *
 * Transfers call `removeCall` as soon as the REFER is sent, so the terminate
 * path never sees these legs and they used to leave no history at all. This
 * runs while the call and its peer connection are still around.
 */
const captureTransferredLeg = (
  call: CallSession,
  data: {
    scenario: 'blind-transfer' | 'voicemail-transfer' | 'warm-transfer';
    interactionId: string;
    relatedCallIds?: string[];
    transferType: 'blind' | 'voicemail' | 'warm';
    destination: string | null;
    relatedCallId?: string | null;
  }
): void => {
  try {
    DebugService.getInstance().capture(call.id, {
      phoneNumber: call.phoneNumber,
      direction: call.direction,
      state: 'ended',
      startTime: call.startTime,
      answerTime: call.answerTime,
      endTime: new Date().toISOString(),
      scenario: data.scenario,
      endedBy: 'transferred',
      interactionId: data.interactionId,
      relatedCallIds: data.relatedCallIds,
      transfer: {
        type: data.transferType,
        destination: data.destination,
        // 'referred', not 'accepted': all we know at this point is that the
        // REFER went out. Only a NOTIFY can upgrade this.
        outcome: 'referred',
        relatedCallId: data.relatedCallId ?? null,
      },
    });
  } catch (error) {
    logger.warn('Failed to capture transfer diagnostics', {
      callId: call.id,
      error: (error as Error).message,
    });
  }
};

/**
 * Watch the NOTIFYs the far end sends about a REFER.
 *
 * Without this the code treats "REFER sent" as "transfer succeeded", and so
 * would the panel — which is exactly the wrong answer for the most common
 * report, "it said it transferred but the call dropped".
 *
 * Two things matter for safety. sip.js stops auto-accepting NOTIFYs once an
 * `onNotify` callback is supplied, so this callback MUST accept them itself or
 * the far end is left retransmitting. And nothing here may throw back into the
 * SIP stack.
 */
const buildReferObserver = (context: {
  callId: string;
  relatedCallId?: string;
  transferType: 'blind' | 'voicemail' | 'warm';
}) => ({
  onNotify: (notification: Notification): void => {
    // Accept first, log second. Answering the far end is the part that must
    // happen even if everything below it fails.
    void notification.accept().catch((error: Error) => {
      logger.warn('Failed to accept REFER NOTIFY', {
        callId: context.callId,
        error: error.message,
      });
    });

    try {
      const sipfrag = String(notification.request?.body ?? '').trim();
      const status = sipfrag.split('\n')[0]?.trim() || 'unknown';

      emit(`Transfer progress: ${status}`, {
        callId: context.callId,
        relatedCallId: context.relatedCallId,
        transferType: context.transferType,
        sipfrag,
      });

      DebugService.getInstance().recordTransferOutcome(context.callId, {
        response: status,
      });
    } catch (error) {
      logger.warn('Failed to record REFER NOTIFY', {
        callId: context.callId,
        error: (error as Error).message,
      });
    }
  },
});

export class TransferHandler {
  /**
   * Execute a blind transfer for the given call.
   *
   * Algorithm:
   * 1. Validate call exists and has an active SIP session in Established state
   * 2. Validate SIP domain is available from store config
   * 3. Build and validate the transfer URI
   * 4. Send SIP REFER via session.refer()
   * 5. Remove the call from the store on success
   *
   * @param callId - The ID of the call to transfer
   * @param destination - The transfer destination (phone number or internal route)
   * @throws Error if preconditions are not met or REFER fails
   */
  async execute(callId: string, destination: string): Promise<void> {
    const store = useSoftphoneStore.getState();
    const call = store.activeCalls.get(callId);

    // A voicemail transfer is the same REFER with a '*'-prefixed destination,
    // so the type is derived rather than threaded through every caller.
    const transferType = destination.startsWith('*') ? 'voicemail' : 'blind';

    // ASSERT: call exists and has active SIP session
    if (!call || !call.session) {
      emit('Transfer not sent', {
        callId,
        transferType,
        destination,
        reason: 'call not found or no session',
      });
      throw new Error(`Cannot transfer: call ${callId} not found or no session`);
    }

    if (call.session.state !== SipSessionState.Established) {
      emit('Transfer not sent', {
        callId,
        transferType,
        destination,
        reason: `session state is ${call.session.state}, expected Established`,
      });
      throw new Error(`Cannot transfer: call ${callId} session not established`);
    }

    // Step 1: Build the SIP REFER target URI
    const config = store.config;
    if (!config?.domain) {
      emit('Transfer not sent', {
        callId,
        transferType,
        destination,
        reason: 'SIP domain not available',
      });
      throw new Error('Cannot transfer: SIP domain not available');
    }

    const targetUri = buildTransferUri(destination, config.domain);
    const uri = UserAgent.makeURI(targetUri);

    if (!uri) {
      emit('Transfer not sent', {
        callId,
        transferType,
        destination,
        targetUri,
        reason: 'invalid URI',
      });
      throw new Error(`Cannot transfer: invalid URI ${targetUri}`);
    }

    // Step 2: Send SIP REFER
    logger.info('Sending blind transfer REFER', { callId, targetUri });
    emit('Transfer requested', {
      callId,
      transferType,
      destination,
      targetUri,
    });

    try {
      await call.session.refer(uri, buildReferObserver({ callId, transferType }));
    } catch (error) {
      emit('Transfer request failed', {
        callId,
        transferType,
        targetUri,
        error: (error as Error).message,
      });
      throw error;
    }

    // Step 3: Clean up - call is now transferred
    logger.info('Blind transfer REFER sent successfully', { callId });

    captureTransferredLeg(call, {
      scenario: transferType === 'voicemail' ? 'voicemail-transfer' : 'blind-transfer',
      interactionId: callId,
      transferType,
      destination,
    });

    // Before the removal: afterwards nothing can tell this from a hang-up.
    store.updateCall(callId, { endedBy: 'transferred' });
    store.removeCall(callId);
  }

  /**
   * Execute a warm (attended) transfer by sending SIP REFER with Replaces.
   *
   * Logic:
   * 1. Validate both calls exist and have active SIP sessions in Established state
   * 2. Send SIP REFER with Replaces: consultationSession.refer(originalSession)
   * 3. Remove both calls from the store on success
   *
   * @param originalCallId - The ID of the original call (on hold)
   * @param consultationCallId - The ID of the consultation call (active)
   * @throws Error if preconditions are not met or REFER fails
   */
  async executeWarmTransfer(originalCallId: string, consultationCallId: string): Promise<void> {
    const store = useSoftphoneStore.getState();
    const originalCall = store.activeCalls.get(originalCallId);
    const consultationCall = store.activeCalls.get(consultationCallId);

    const legs = { originalCallId, consultationCallId };

    if (!originalCall?.session || !consultationCall?.session) {
      emit('Warm transfer not sent', {
        ...legs,
        callId: consultationCallId,
        reason: 'missing call sessions',
      });
      throw new Error('Cannot complete warm transfer: missing call sessions');
    }

    if (consultationCall.session.state !== SipSessionState.Established) {
      emit('Warm transfer not sent', {
        ...legs,
        callId: consultationCallId,
        reason: `consultation session state is ${consultationCall.session.state}`,
      });
      throw new Error('Cannot complete warm transfer: consultation not established');
    }

    if (originalCall.session.state !== SipSessionState.Established) {
      emit('Warm transfer not sent', {
        ...legs,
        callId: consultationCallId,
        reason: `original session state is ${originalCall.session.state}`,
      });
      throw new Error('Cannot complete warm transfer: original call session not established');
    }

    // SIP REFER with Replaces: consultation session refers TO the original session
    // This tells the server to connect the original caller to the consultation recipient
    logger.info('Sending warm transfer REFER', { originalCallId, consultationCallId });
    emit('Warm transfer requested', {
      ...legs,
      // The REFER travels on the consultation leg, so that is the call it
      // belongs to. Which leg carried it is the first question when a warm
      // transfer is refused.
      callId: consultationCallId,
      transferType: 'warm',
      destination: originalCall.phoneNumber,
    });

    try {
      await consultationCall.session.refer(
        originalCall.session,
        buildReferObserver({
          callId: consultationCallId,
          relatedCallId: originalCallId,
          transferType: 'warm',
        })
      );
    } catch (error) {
      emit('Warm transfer request failed', {
        ...legs,
        callId: consultationCallId,
        error: (error as Error).message,
      });
      throw error;
    }

    // Both calls are now handled by the server — remove from local state
    logger.info('Warm transfer REFER sent successfully');

    // One interaction, two legs. Keyed on the original call so the pair stays
    // together in the history no matter which leg is read first.
    captureTransferredLeg(consultationCall, {
      scenario: 'warm-transfer',
      interactionId: originalCallId,
      relatedCallIds: [originalCallId],
      transferType: 'warm',
      destination: originalCall.phoneNumber,
      relatedCallId: originalCallId,
    });
    captureTransferredLeg(originalCall, {
      scenario: 'warm-transfer',
      interactionId: originalCallId,
      relatedCallIds: [consultationCallId],
      transferType: 'warm',
      destination: consultationCall.phoneNumber,
      relatedCallId: consultationCallId,
    });

    store.updateCall(consultationCallId, { endedBy: 'transferred' });
    store.updateCall(originalCallId, { endedBy: 'transferred' });
    store.removeCall(consultationCallId);
    store.removeCall(originalCallId);
  }
}
